/**
 * Content for How to Join pages
 */
export const HOW_TO_JOIN_CONTENT = {
  TITLE: 'How to Join',
  INTRO:
    "At MarQ Capital, we welcome curious and innovative minds. With strong ethical values, we tackle complex financial challenges with passion and commitment. Joining us means working with top professionals, learning continuously, and making a real impact. If you're ready to help shape the future of finance, we’d love to hear from you.",
  SECTIONS: {
    CAREER_DEVELOPMENT: {
      TITLE: 'Career Development',
      TEXT: [
        "At MarQ Capital, we believe that professional growth is a continuous journey and that learning should be an ongoing and exciting process. To support the development of our team, we offer comprehensive training programs, mentorship opportunities, and clear career progression paths. Our focus on both technical skills and leadership development ensures that you'll gain valuable experience that extends beyond your immediate role."
      ],
      HIGHLIGHT:
        'At MarQ Capital, we believe that the diversity of our employees is fundamental to our strength and success. We are committed to supporting our employees at work and in life. In addition to attractive financial compensation packages, we offer comprehensive healthcare benefits, flexible work arrangements, family-friendly policies, and a variety of wellbeing programs.',
      DISCLAIMER:
        'MarQ Capital members do not discriminate in employment matters on the basis of race, color, religion, gender, gender identity, pregnancy, national origin, age, eligibility for military status, disability, or sexual orientation. To understand how we handle Personal Data, if you are in the EU click here and if you are in the UK click here to read our Recruitment Personal Data Policy Notice.'
    },
    CHOOSE_YOUR_PATH: {
      TITLE: 'Choose Your Path'
    }
  },
  FOOTER_NOTE:
    "We are always looking for exceptional talent. If you don't see a role that matches your profile, feel free to send us your resume for future opportunities."
};

export const INTERVIEW_CONTENT = {
  TITLE: 'Interview Process',
  DESCRIPTION:
    'Learn about our interview process, what we look for in candidates, and how to prepare for your discussions with our team.',
  INTRO: [
    "Interviewing is a crucial part of our hiring process. It's an opportunity for us to get to know you better, and for you to gain deeper insight into our firm and the role you're applying for.",
    'We value the skills, experiences, and perspectives that each candidate brings, and our interview process is designed to assess not only your technical expertise but also your cultural fit, problem-solving abilities, and potential for growth within our organization.'
  ],
  COMPELLING_CANDIDATES: {
    TITLE: 'What We Look for in Compelling Candidates',
    INTRO:
      "The candidates we seek are those who excel in a dynamic and challenging environment. Here's what we look for:",
    ITEMS: [
      {
        title: 'Uphold High Standards',
        text: "We believe in excellence in all that we do. We're looking for individuals who hold themselves accountable to the highest standards and consistently strive for exceptional results."
      },
      {
        title: 'Analytical Rigor',
        text: "Whether you're solving complex problems or making decisions, we value candidates who can approach challenges with a critical and analytical mindset, backed by data-driven insights."
      },
      {
        title: 'Clear and Concise Communication',
        text: 'We operate in an environment where clarity and precision are key. Candidates who can communicate complex ideas in a straightforward and effective manner are highly valued.'
      },
      {
        title: 'Collaboration and Teamwork',
        text: 'At MarQ Capital, collaboration is essential to our success. We look for individuals who thrive in a team environment, contribute positively to group dynamics, and can work effectively across different functions.'
      },
      {
        title: 'Intellectual Curiosity',
        text: 'A genuine desire to learn and grow is essential. We seek individuals who are not only experts in their fields but are also constantly looking for ways to expand their knowledge and skills.'
      }
    ]
  },
  PROCESS: {
    TITLE: 'Our Interview Process',
    INTRO:
      "We want you to feel prepared and confident throughout the hiring process, and we are here to support you every step of the way. Here's an overview of what to expect:",
    STEPS: [
      {
        title: 'Application Review',
        text: 'The process begins with an in-depth review of your application, where we assess your qualifications, experience, and potential fit with our team.'
      },
      {
        title: 'Phone Interview',
        text: "If your application is shortlisted, we'll schedule a phone interview. This initial conversation is an opportunity for us to learn more about your background, skills, and career aspirations, and for you to ask questions about the role and our firm."
      },
      {
        title: 'Virtual Interviews',
        text: "Successful phone interviews will be followed by one or more virtual interviews. These will typically involve discussions with key team members, where we'll delve deeper into your expertise, experiences, and how you approach challenges."
      },
      {
        title: 'Case Study or Writing Sample',
        text: 'Depending on the role, you may be asked to complete a case study, writing sample, or code sample. These tasks are designed to evaluate your problem-solving and analytical abilities.'
      },
      {
        title: 'Reference Checks',
        text: "We will reach out to your references to validate your experience and skills and to gather insights about how you've contributed to previous teams."
      },
      {
        title: 'Final Offer',
        text: "If all goes well, the final step will be an offer to join the team. We'll provide you with all the information you need to make an informed decision and welcome you to MarQ Capital."
      }
    ]
  },
  PREPARATION: {
    TITLE: 'Preparing for the Interview',
    TEXT: [
      'We understand that interviews can be a daunting process, but we want you to feel confident and prepared. To help you put your best foot forward, we recommend reviewing our guide to preparing for an interview, which offers insights into our interview style and valuable tips for success.',
      "Our interview process is thorough, but it's also designed to be a positive experience for both you and us. We believe that the best candidates are those who not only demonstrate their skills but also show their genuine personality and alignment with our values."
    ]
  },
  FOOTER_NOTE:
    "We are always looking for exceptional talent. If you don't see a role that matches your profile, feel free to send us your resume for future opportunities."
};

export const INTERNSHIP_CONTENT = {
  TITLE: 'Internship Program',
  DESCRIPTION:
    'Discover opportunities for students to gain hands-on experience and contribute to meaningful projects at MarQ Capital.',
  INTRO: [
    'At MarQ Capital, we believe that internships are an invaluable opportunity for exceptional students from all backgrounds to gain hands-on experience and insights into the world of finance and technology. Our internship programs are designed to provide meaningful work, mentorship, and learning opportunities that will help shape the future leaders of our industry.',
    "As an intern at MarQ Capital, you'll not only be immersed in our daily operations but will also have the chance to work alongside experienced professionals who are passionate about sharing their knowledge and helping you develop your skills. Our goal is to ensure that every intern walks away with a deeper understanding of our business, practical skills that are transferable across industries, and valuable connections that will support their future career endeavors."
  ],
  EXPECTATIONS: {
    TITLE: 'What to Expect as an Intern',
    INTRO:
      'Our internship program offers comprehensive exposure to different aspects of our business:',
    ITEMS: [
      {
        title: 'Challenging and Impactful Work',
        text: "During your internship, you'll have the opportunity to dive into some of the most intellectually stimulating problems in finance and technology. Whether you're working on quantitative research, software development, or financial analysis, you'll be tackling real problems and making meaningful contributions to our projects."
      },
      {
        title: 'Mentorship and Learning',
        text: 'At MarQ Capital, we understand the importance of mentorship in shaping the next generation of leaders. Throughout your internship, you will be paired with experienced professionals who will guide your work, provide feedback, and help you navigate the complexities of our industry.'
      },
      {
        title: 'Tailored Educational Programming',
        text: 'We believe in continuous learning, which is why our internship program includes tailored educational sessions that cover key aspects of finance, technology, and professional development. These sessions are designed to complement your practical experience and provide a well-rounded understanding of our business.'
      },
      {
        title: 'Community-Building and Networking',
        text: "Our interns are not just part of a program, they're part of the MarQ Capital community. Throughout your time with us, you'll have the opportunity to engage with colleagues across different teams, build lasting relationships, and expand your professional network."
      },
      {
        title: 'Real-World Impact',
        text: 'We don\'t see our interns as "temporary help", we see them as future leaders who can make a real difference. Your contributions are valued, and you\'ll be encouraged to share your ideas and perspectives, knowing that they could influence our approach to solving problems.'
      }
    ]
  },
  WHY_JOIN: {
    TITLE: 'Why Join Our Internship Program?',
    INTRO: "Here's why our internship program stands out:",
    ITEMS: [
      {
        title: 'Diverse Exposure',
        text: 'At MarQ Capital, you will have the chance to work in a fast-paced, intellectually stimulating environment that blends finance, technology, and innovation. Our internship programs are designed to provide a comprehensive view of our business, allowing you to explore different aspects and identify areas where your skills and interests align.'
      },
      {
        title: 'A Path to Full-Time Opportunities',
        text: 'Successful interns are often considered for full-time roles at MarQ Capital after graduation. Many of our current employees began their careers as interns and have since grown into leadership positions within the firm.'
      },
      {
        title: 'Work-Life Balance and Wellbeing',
        text: "We believe in a balanced approach to work and life, understanding that a fulfilling career is built on well-being and a healthy work environment. As an intern, you'll experience firsthand how we prioritize this balance through our policies, practices, and culture."
      }
    ]
  },
  WHO_WE_LOOK_FOR: {
    TITLE: "Who We're Looking For",
    TEXT: [
      "We're seeking motivated, curious, and driven students who have a passion for learning and a desire to contribute to a fast-paced, evolving environment. While we value academic excellence, we also recognize that exceptional talent comes in many forms and from diverse educational backgrounds.",
      'Successful candidates will typically have:'
    ],
    SKILLS: [
      { text: 'Strong problem-solving and analytical skills' },
      { text: 'A keen interest in finance, technology, or both' },
      {
        text: 'Excellent communication skills and the ability to work collaboratively'
      },
      {
        text: 'A growth mindset and a genuine curiosity to learn and develop new skills'
      },
      {
        text: 'Proven ability to work in a fast-paced, high-pressure environment (either through academic or extracurricular activities)'
      }
    ],
    CONCLUSION:
      "Whether you're studying finance, computer science, engineering, mathematics, economics, or another related field, we encourage students from all backgrounds to apply. We value diverse perspectives and believe that a variety of educational experiences contributes to a stronger, more creative team."
  },
  APPLY: {
    TITLE: 'Apply for Our Internship Program',
    TEXT: "If you're ready to take the next step in your career and contribute to solving complex problems at the forefront of finance and technology, we encourage you to apply for one of our internship opportunities listed below. We look forward to meeting you and exploring how your unique talents can contribute to our team."
  },
  FOOTER_NOTE:
    "We are always looking for exceptional talent. If you don't see a role that matches your profile, feel free to send us your resume for future opportunities."
};

export const PREPARING_INTERVIEW_CONTENT = {
  TITLE: 'Preparing for an Interview with MarQ Capital',
  INTRO: [
    'At MarQ Capital, we are dedicated to finding, recruiting, and nurturing exceptional talent. We understand that the process of finding the right next step in your career can be both exciting and challenging. Our aim is to make the interview experience as informative, comfortable, and productive as possible for all candidates.',
    'We hope the following guidance helps you prepare for your interview with us.'
  ],
  WHAT_WERE_LOOKING_FOR: {
    TITLE: "What We're Looking For",
    TEXT: "At MarQ Capital, we believe that strong talent isn't limited to experience in finance alone. While financial knowledge is valuable, we are looking for individuals who bring a range of qualities and skills that align with our values and culture. Here's what we value in our candidates:",
    ITEMS: [
      {
        title: 'Uphold High Standards',
        text: 'We look for individuals who consistently uphold high standards in everything they do. This includes treating colleagues and clients with respect and professionalism, delivering quality work, and maintaining integrity in all interactions.'
      },
      {
        title: 'Analyze Rigorously',
        text: "We value individuals who approach problems with analytical rigor. We're eager to hear about the learning experiences you've had and how your thinking evolved as a result. Whether you're discussing a complex academic project, a work challenge, or a personal pursuit, we are interested in how you analyze situations and make decisions."
      },
      {
        title: 'Communicate Clearly and Concisely',
        text: "Clear communication is key at MarQ Capital. We value candidates who can express their ideas in a structured, logical manner, whether it's explaining complex concepts, sharing insights, or responding to challenging questions."
      },
      {
        title: 'Thrive on Collaboration',
        text: 'Collaboration is central to our work environment. We seek candidates who excel in team settings—who share ideas, listen actively, and help drive collective progress.'
      },
      {
        title: 'Demonstrate Intellectual Curiosity',
        text: "Whether it's through your professional experience, extracurricular activities, or personal interests, we want to know about what drives you. We value candidates who are continually seeking to learn and grow, who ask insightful questions, and who are open to new perspectives."
      }
    ]
  },
  GETTING_READY: {
    TITLE: 'Getting Ready for Your Interview',
    TEXT: "The interview is a chance for us to learn more about your experiences, your approach to problem-solving, and how you might contribute to MarQ Capital. Here's how you can prepare effectively:",
    ITEMS: [
      {
        title: 'Tell Your Story',
        text: 'Your experience is more than just a list of qualifications. What are the key moments that have shaped your career or academic journey? What drives you? How have your experiences prepared you for a role at MarQ Capital? Be ready to articulate your narrative in a compelling way.'
      },
      {
        title: 'Dig Deep into Your Experiences',
        text: 'Be ready to discuss the details behind the experiences on your resume. What challenges have you faced, and what did you learn from them? How did you work within teams or lead others? What technical skills or methodologies did you apply, and with what results?'
      },
      {
        title: 'Ask Questions',
        text: 'The interview is a two-way street, and we encourage you to ask questions. This is your opportunity to gain deeper insight into MarQ Capital, our values, and how you might fit into our team. Thoughtful questions demonstrate your interest and engagement.'
      },
      {
        title: 'Practice Your Responses',
        text: "To communicate your experiences and thoughts effectively, practice telling your story out loud. You can rehearse with a friend, career counselor, or in front of a mirror. This helps you refine your answers and ensure they're concise and substantive."
      }
    ]
  },
  WHAT_WE_ASK: {
    TITLE: 'What We Might Ask You',
    TEXT: 'Our interviews are designed to understand how you think, how you solve problems, and how you might contribute to our team. You can expect a mix of the following question types:',
    ITEMS: [
      {
        title: 'Behavioral Questions',
        text: 'These questions focus on past experiences and how you handled specific situations. Examples include:\n• How has your approach to problem-solving evolved with experience?\n• Describe a time when you had to collaborate with a team on a challenging project.\n• Tell us about a situation where you had to adapt quickly to changing circumstances.\n• How have you handled a situation where you had conflicting priorities or deadlines?'
      },
      {
        title: 'Situational Questions',
        text: 'These questions are designed to assess how you would handle hypothetical scenarios relevant to the role. Examples include:\n• How would you approach building relationships with a new team?\n• What would you do if you identified a significant error in a project just before a deadline?\n• How would you prioritize tasks if you were managing multiple projects simultaneously?'
      },
      {
        title: 'Case Studies',
        text: 'These questions simulate real-world challenges and allow you to demonstrate how you approach complex problems. For example:\n• Scenario: A company is deciding whether to expand into a new market. What factors would you consider, and what information would you gather to make a recommendation?'
      }
    ],
    ADDITIONAL_TEXT:
      'For technical roles, expect questions that assess your analytical thinking, problem-solving abilities, and understanding of key concepts. For roles in quantitative finance, you might be asked about statistical methods, financial models, or programming skills. For roles in software engineering, expect questions about coding practices, system design, or technology architecture.'
  },
  AFTER_INTERVIEW: {
    TITLE: 'After the Interview',
    TEXT: 'The hiring process at MarQ Capital Limited is thorough, as we take great care in selecting candidates who are not only highly skilled but also fit well with our culture and values. The typical steps in our process include:',
    STEPS_LEFT: [
      { text: 'Application Review' },
      { text: 'Virtual Interview(s)' },
      { text: 'Reference Checks' }
    ],
    STEPS_RIGHT: [
      { text: 'Phone Interview' },
      { text: 'In-person Interview' },
      { text: 'Offer' }
    ],
    CLOSING_TEXT: [
      "The process may take several weeks, so we appreciate your patience. We will keep you updated as the process moves along. If you have any questions or would like more information at any stage, please don't hesitate to reach out to your recruiter.",
      "We encourage you to take some time to learn more about MarQ Capital and our work. We're excited to meet with you and explore how you can contribute to our continued growth and success."
    ]
  },
  FOOTER_TEXT:
    "This Interview Preparation Guide has been tailored to reflect MarQ Capital's approach to recruitment, emphasizing collaboration, intellectual curiosity, and a drive for excellence. We look forward to meeting you and learning more about how your unique talents and experiences could contribute to our team."
};

export const EU_RECRUITMENT_POLICY = {
  TITLE:
    'Notice on the Privacy of Personal Data for Recruitment in the European Union',
  LAST_UPDATED: 'Last updated: May 1, 2025',
  COMPANY_DATA: {
    mqcapital: {
      companyName: 'MarQ Capital (EU)',
      privacyEmail: 'privacy@mqcapital.co.uk',
      recruitingEmail: 'recruiting@mqcapital.co.uk',
      intro:
        'The purpose of this personal data privacy notice is to inform you about how your personal data (i.e., information that identifies you directly or indirectly) is processed by MarQ Capital (EU), ("we", "us", "our") when you apply for a job at MarQ Capital (EU). We are responsible for handling your personal data.',
      rightToObject:
        'Please note that you have the right to object to the processing of your personal data when such processing is carried out based on our legitimate interests. Unless you provide us with explicit consent to do so, we will not use your personal data for purposes unrelated to those mentioned above, such as marketing third-party products. If we are legally or contractually required to process certain personal data and you do not provide it when requested, we may not be able to process your application.',
      internationalTransfers:
        'MarQ Capital (EU) may disclose your personal data for the purposes described above to recipients located outside the European Economic Area ("EEA"), including in the UK and United States. These countries may not have data protection laws equivalent to those in the EEA. We will make such transfers as necessary to take certain steps required to consider your job application at MarQ Capital (EU) and if applicable, for MarQ Capital (EU) to take steps necessary to enter an employment contract with you.',
      standardContracts:
        'In some cases, we may transfer candidates\' personal data within our group under agreements substantially like EU Standard Contractual Clauses ("Standard Contracts"). You can request more information about international transfers, including a copy of any agreements, by using the contact details below.',
      retentionProcedures:
        'We will retain your personal data for prospective recruitment activities, including seeking candidates for job opportunities and organizing events with potential candidates in line with our internal retention procedures.',
      complaintInfo:
        'If you wish to exercise any of these rights, please contact us using the details provided below. You also have the right to lodge a complaint regarding the processing of your personal data with your local Data Protection Authority. Contact details for each European authority can be found here.',
      periodicReview:
        'We periodically review this notice and may modify it. If changes are made, we remain committed to keeping your personal data secure. If substantial changes are made to this notice, we will notify you via our website.'
    },
    spinnaker: {
      companyName: 'Spinnaker Algo Systems LTD (EU)',
      privacyEmail: 'privacy@spinnakeralgo.com',
      recruitingEmail: 'recruiting@spinnakeralgo.com',
      intro:
        'The purpose of this personal data privacy notice is to inform you about how your personal data (i.e., information that identifies you directly or indirectly) is processed by Spinnaker Algo Systems LTD (EU), ("we", "us", "our") when you apply for a job at Spinnaker Algo Systems LTD (EU). We are responsible for handling your personal data.',
      rightToObject:
        'Please note that you have the right to object to the processing of your personal data when such processing is carried out based on our legitimate interests. Unless you provide us with explicit consent to do so, we will not use your personal data for purposes unrelated to those mentioned above, such as marketing third-party products. If we are legally or contractually required to process certain personal data and you do not provide it when requested, we may not be able to process your application.',
      internationalTransfers:
        'Spinnaker Algo Systems LTD (EU) may disclose your personal data for the purposes described above to recipients located outside the European Economic Area ("EEA"), including in the UK and United States. These countries may not have data protection laws equivalent to those in the EEA. We will make such transfers as necessary to take certain steps required to consider your job application at Spinnaker Algo Systems LTD (EU) and if applicable, for Spinnaker Algo Systems LTD (EU) to take steps necessary to enter an employment contract with you.',
      standardContracts:
        'In some cases, we may transfer candidates\' personal data within our group under agreements substantially like EU Standard Contractual Clauses ("Standard Contracts"). You can request more information about international transfers, including a copy of any agreements, by using the contact details below.',
      retentionProcedures:
        'We will retain your personal data for prospective recruitment activities, including seeking candidates for job opportunities and organizing events with potential candidates in line with our internal retention procedures.',
      complaintInfo:
        'If you wish to exercise any of these rights, please contact us using the details provided below. You also have the right to lodge a complaint regarding the processing of your personal data with your local Data Protection Authority. Contact details for each European authority can be found here.',
      periodicReview:
        'We periodically review this notice and may modify it. If changes are made, we remain committed to keeping your personal data secure. If substantial changes are made to this notice, we will notify you via our website.'
    }
  },
  DATA_COLLECTION: {
    TITLE: 'What information do we collect?',
    INTRO: 'We may collect some or all of the following information about you:',
    ITEMS: [
      {
        text: 'Your name, address, and contact details, including email address and phone number.'
      },
      {
        text: 'Detailed information about your qualifications, skills, experience, education, and employment history.'
      },
      {
        text: 'Samples of work products, such as examples of software code or written work.'
      },
      {
        text: 'Information about your nationality, residency status, or right to work in the United Kingdom (or another relevant jurisdiction).'
      },
      { text: 'Details about your current or expected future remuneration.' },
      {
        text: 'Information you may voluntarily provide about your gender identity, race or ethnicity, or sexual orientation.'
      },
      {
        text: 'Information about criminal convictions and/or offences, as identified during a personal inquiry.'
      },
      { text: 'Any other information that you voluntarily provide to us.' }
    ],
    THIRD_PARTY_TEXT:
      'Sometimes, we collect personal data about you from third parties. For example, we may:',
    THIRD_PARTY_ITEMS: [
      { text: 'Receive some of the above information from a recruiter.' },
      {
        text: 'Obtain professional references from a former employer or another referee you provide.'
      },
      {
        text: 'Engage a background check service provider to conduct employment, credit, and/or criminal background checks.'
      }
    ]
  },
  WHY_PROCESS_DATA: {
    TITLE: 'Why do we process your personal data?',
    INTRO: 'Providing your personal data is necessary for us to:',
    ITEMS: [
      {
        title: 'Legal Obligations',
        text: 'Fulfil [COMPANY NAME] legal obligations (e.g., verifying the eligibility of the selected candidate to work in the United Kingdom or another relevant jurisdiction).'
      },
      {
        title: 'Contract Preparation',
        text: 'Take the necessary preparatory steps for [COMPANY NAME] to enter an employment contract with you (if your application is successful).'
      },
      {
        title: 'Application Processing',
        text: 'Process your application and consider you for a role at [COMPANY NAME] (including communicating with you), administer, manage, and improve our recruitment processes including record-keeping, optimize the sourcing and selection of candidates, contact you regarding other employment opportunities, and conduct pre-employment screening.'
      }
    ],
    ADDITIONAL_TEXT:
      'We carry out these activities in our legitimate interest to manage our recruitment function, identify candidates, and hire employees. When conducting background checks during the hiring process, we have a legitimate interest in ensuring the integrity of our hiring process and the individuals we hire.',
    VOLUNTARY_TEXT:
      'You will have the opportunity to voluntarily provide information about your gender identity, race or ethnicity, or sexual orientation. We use this information to monitor and evaluate the effectiveness of our diversity initiatives and to be an inclusive and diverse workplace. Any such information will be held securely, maintained separately from documentation used in making recruitment decisions, and accessed only by authorized employees. Providing this information is completely voluntary, and your decision on whether to provide it will not negatively impact your candidacy in any way.'
  },
  RIGHT_TO_OBJECT: {
    TITLE: 'Your right to object',
    TEXT: 'Please note that you have the right to object to the processing of your personal data when such processing is carried out based on our legitimate interests. Unless you provide us with explicit consent to do so, we will not use your personal data for purposes unrelated to those mentioned above, such as marketing third-party products. If we are legally or contractually required to process certain personal data and you do not provide it when requested, we may not be able to process your application.'
  },
  ACCESS_TO_DATA: {
    TITLE: 'Who has access to your personal data?',
    INTRO:
      'In connection with the purposes described above, we may disclose your personal data to:',
    ITEMS: [
      {
        text: 'Our affiliates, service providers (e.g., IT service providers, background check providers), consultants, and advisors.'
      },
      { text: 'Fraud prevention agencies and law enforcement agencies.' },
      {
        text: 'Courts, regulatory entities (governmental and non-governmental), and ombudsmen.'
      },
      {
        text: 'Any third party acquiring or interested in acquiring or securing all or part of our assets, shares, partnership interests, and/or member interests; or succeeding us in conducting all or part of our business through merger, acquisition, reorganization, or otherwise.'
      },
      {
        text: 'As required or permitted by law, including complying with subpoenas or similar legal processes or government requests; or when we believe in good faith that disclosure is legally required or necessary to protect our rights and property.'
      }
    ],
    INTERNATIONAL_TEXT: [
      '[COMPANY NAME] (EU) may disclose your personal data for the purposes described above to recipients located outside the European Economic Area ("EEA"), including in the UK and United States. These countries may not have data protection laws equivalent to those in the EEA. We will make such transfers as necessary to take certain steps required to consider your job application at [COMPANY NAME] (EU) and if applicable, for [COMPANY NAME] (EU) to take steps necessary to enter an employment contract with you',
      'In some cases, we may transfer candidates personal data within our group under agreements substantially like EU Standard Contractual Clauses ("Standard Contracts"). You can request more information about international transfers, including a copy of any agreements, by using the contact details below.'
    ]
  },
  DATA_RETENTION: {
    TITLE: 'How long do we retain your personal data?',
    TEXT: 'We will retain your personal data for prospective recruitment activities, including seeking candidates for job opportunities and organizing events with potential candidates in line with our internal retention procedures. The criteria used to determine retention periods include:',
    ITEMS: [
      {
        text: 'The period during which personal data is needed for evaluating your application and other recruitment activities.'
      },
      { text: 'The type of personal data collected.' },
      {
        text: 'Whether we are subject to a legal or contractual obligation requiring retention of the data (e.g., employment-related retention laws or government orders).'
      }
    ]
  },
  YOUR_RIGHTS: {
    TITLE: 'Your rights',
    INTRO: 'You have the right to:',
    ITEMS: [
      {
        text: 'Request access to, rectification of, or deletion of your personal data.'
      },
      {
        text: 'Obtain restriction of processing or object to processing of your personal data.'
      },
      {
        text: 'Request that a copy of your personal data be provided to yourself or a third party in digital format.'
      },
      { text: 'Withdraw consent for processing of your personal data.' }
    ],
    ADDITIONAL_TEXT:
      'We will respond to your request in writing—or orally if requested—as soon as possible and within one month at most after receiving it. In exceptional cases, this period may be extended by up to two months; if so, we will inform you of the reason.'
  },
  QUESTIONS: {
    TITLE: 'Questions, requests or concerns',
    TEXT: 'All questions, requests or concerns regarding this notice or related processing of your personal data—including all requests detailed in the "Your Rights" section above—should be sent via email to'
  },
  FOOTER_NOTE:
    'This policy is provided for informational purposes and does not create any rights or obligations beyond those provided by applicable laws.'
};

export const UK_RECRUITMENT_POLICY = {
  TITLE:
    'Notice on the Privacy of Personal Data for Recruitment in the United Kingdom',
  LAST_UPDATED: 'Last updated: May 1, 2025',
  COMPANY_DATA: {
    mqcapital: {
      companyName: 'MarQ Capital (UK)',
      privacyEmail: 'privacy@mqcapital.co.uk',
      recruitingEmail: 'recruiting@mqcapital.co.uk',
      intro:
        'The purpose of this personal data privacy notice is to inform you about how your personal data (i.e., information that identifies you directly or indirectly) is processed by MarQ Capital (UK), ("we", "us", "our") when you apply for a job at MarQ Capital (UK). We are responsible for handling your personal data.',
      rightToObject:
        'Please note that you have the right to object to the processing of your personal data when such processing is carried out based on our legitimate interests. Unless you provide us with explicit consent to do so, we will not use your personal data for purposes unrelated to those mentioned above, such as marketing third-party products. If we are legally or contractually required to process certain personal data and you do not provide it when requested, we may not be able to process your application.',
      retentionProcedures:
        'We will retain your personal data for prospective recruitment activities, including seeking candidates for job opportunities and organizing events with potential candidates in line with our internal retention procedures.',
      complaintInfo:
        "If you wish to exercise any of these rights, please contact us using the details provided below. You also have the right to lodge a complaint regarding the processing of your personal data with the Information Commissioner's Office (ICO), the UK supervisory authority for data protection issues.",
      periodicReview:
        'We periodically review this notice and may modify it. If changes are made, we remain committed to keeping your personal data secure. If substantial changes are made to this notice, we will notify you via our website.'
    },
    spinnaker: {
      companyName: 'Spinnaker Algo Systems LTD (UK)',
      privacyEmail: 'privacy@spinnakeralgo.com',
      recruitingEmail: 'recruiting@spinnakeralgo.com',
      intro:
        'The purpose of this personal data privacy notice is to inform you about how your personal data (i.e., information that identifies you directly or indirectly) is processed by Spinnaker Algo Systems LTD (UK), ("we", "us", "our") when you apply for a job at Spinnaker Algo Systems LTD (UK). We are responsible for handling your personal data.',
      rightToObject:
        'Please note that you have the right to object to the processing of your personal data when such processing is carried out based on our legitimate interests. Unless you provide us with explicit consent to do so, we will not use your personal data for purposes unrelated to those mentioned above, such as marketing third-party products. If we are legally or contractually required to process certain personal data and you do not provide it when requested, we may not be able to process your application.',
      retentionProcedures:
        'We will retain your personal data for prospective recruitment activities, including seeking candidates for job opportunities and organizing events with potential candidates in line with our internal retention procedures.',
      complaintInfo:
        "If you wish to exercise any of these rights, please contact us using the details provided below. You also have the right to lodge a complaint regarding the processing of your personal data with the Information Commissioner's Office (ICO), the UK supervisory authority for data protection issues.",
      periodicReview:
        'We periodically review this notice and may modify it. If changes are made, we remain committed to keeping your personal data secure. If substantial changes are made to this notice, we will notify you via our website.'
    }
  },
  DATA_COLLECTION: {
    TITLE: 'What information do we collect?',
    INTRO: 'We may collect some or all of the following information about you:',
    ITEMS: [
      {
        text: 'Your name, address, and contact details, including email address and phone number.'
      },
      {
        text: 'Detailed information about your qualifications, skills, experience, education, and employment history.'
      },
      {
        text: 'Samples of work products, such as examples of software code or written work.'
      },
      {
        text: 'Information about your nationality, residency status, or right to work in the United Kingdom (or another relevant jurisdiction).'
      },
      { text: 'Details about your current or expected future remuneration.' },
      {
        text: 'Information you may voluntarily provide about your gender identity, race or ethnicity, or sexual orientation.'
      },
      {
        text: 'Information about criminal convictions and/or offences, as identified during a personal inquiry.'
      },
      { text: 'Any other information that you voluntarily provide to us.' }
    ],
    THIRD_PARTY_TEXT:
      'Sometimes, we collect personal data about you from third parties. For example, we may:',
    THIRD_PARTY_ITEMS: [
      { text: 'Receive some of the above information from a recruiter.' },
      {
        text: 'Obtain professional references from a former employer or another referee you provide.'
      },
      {
        text: 'Engage a background check service provider to conduct employment, credit, and/or criminal background checks.'
      }
    ]
  },
  WHY_PROCESS_DATA: {
    TITLE: 'Why do we process your personal data?',
    INTRO: 'Providing your personal data is necessary for us to:',
    ITEMS: [
      {
        title: 'Legal Obligations',
        text: 'Fulfil [COMPANY NAME] legal obligations (e.g., verifying the eligibility of the selected candidate to work in the United Kingdom or another relevant jurisdiction).'
      },
      {
        title: 'Contract Preparation',
        text: 'Take the necessary preparatory steps for [COMPANY NAME] to enter an employment contract with you (if your application is successful).'
      },
      {
        title: 'Application Processing',
        text: 'Process your application and consider you for a role at [COMPANY NAME] (including communicating with you), administer, manage, and improve our recruitment processes including record-keeping, optimize the sourcing and selection of candidates, contact you regarding other employment opportunities, and conduct pre-employment screening.'
      }
    ],
    ADDITIONAL_TEXT:
      'We carry out these activities in our legitimate interest to manage our recruitment function, identify candidates, and hire employees. When conducting background checks during the hiring process, we have a legitimate interest in ensuring the integrity of our hiring process and the individuals we hire.',
    VOLUNTARY_TEXT:
      'You will have the opportunity to voluntarily provide information about your gender identity, race or ethnicity, or sexual orientation. We use this information to monitor and evaluate the effectiveness of our diversity initiatives and to be an inclusive and diverse workplace. Any such information will be held securely, maintained separately from documentation used in making recruitment decisions, and accessed only by authorized employees. Providing this information is completely voluntary, and your decision on whether to provide it will not negatively impact your candidacy in any way.'
  },
  RIGHT_TO_OBJECT: {
    TITLE: 'Your right to object',
    TEXT: 'Please note that you have the right to object to the processing of your personal data when such processing is carried out based on our legitimate interests. Unless we can demonstrate compelling legitimate grounds for the processing of your data which override your interests, we will cease the processing in accordance with your objection.'
  },
  ACCESS_TO_DATA: {
    TITLE: 'Who has access to your personal data?',
    INTRO:
      'In connection with the purposes described above, we may disclose your personal data to:',
    ITEMS: [
      {
        text: 'Our affiliates, service providers (e.g., IT service providers, background check providers), consultants, and advisors.'
      },
      { text: 'Fraud prevention agencies and law enforcement agencies.' },
      {
        text: 'Courts, regulatory entities (governmental and non-governmental), and ombudsmen.'
      },
      {
        text: 'Any third party acquiring or interested in acquiring or securing all or part of our assets, shares, partnership interests, and/or member interests; or succeeding to all or part of our business; or that we otherwise reorganize.'
      },
      {
        text: 'As required or permitted by law, including complying with subpoenas or similar legal processes or government requests; or when we believe in good faith that disclosure is necessary to protect our rights, protect your safety or the safety of others, investigate fraud, or respond to a government request.'
      }
    ],
    INTERNATIONAL_TEXT: [
      '[COMPANY NAME] may disclose your personal data for the purposes described above to recipients located outside the European Economic Area ("EEA"), including the United Kingdom which may not have laws (or otherwise rely on approved mechanisms) ensuring an adequate level of protection of personal data. We will make such disclosures only when permitted by data protection laws.',
      'We will make such transfers as necessary to take certain steps required to consider your job application at [COMPANY NAME] and if applicable, for [COMPANY NAME] to take steps necessary to enter an employment contract with you.',
      "In some cases, we may transfer candidates' personal data within our group under agreements substantially like EU Standard Contractual Clauses (\"Standard Contractual Clauses\"). These contracts require the recipient to provide the same level of protection as the EU's data protection laws and expressly provide for individuals' legal rights to be protected. A copy of these Clauses may be obtained by contacting our data privacy team per the contact details below."
    ]
  },
  DATA_RETENTION: {
    TITLE: 'How long do we retain your personal data?',
    TEXT: 'We will retain your personal data for prospective recruitment activities, including seeking candidates for job opportunities and organizing events with potential candidates, in accordance with applicable privacy laws. We determine the retention period for your personal data based on the following criteria:',
    ITEMS: [
      {
        text: 'The period during which personal data is needed for evaluating your application and other recruitment activities.'
      },
      { text: 'The type of personal data collected.' },
      {
        text: 'Whether we are subject to a legal or contractual obligation requiring retention of the data (e.g., employment-related retention laws or government orders).'
      }
    ]
  },
  YOUR_RIGHTS: {
    TITLE: 'Your rights',
    INTRO: 'You have the right to:',
    ITEMS: [
      {
        text: 'Request access to, rectification of, or deletion of your personal data.'
      },
      {
        text: 'Obtain restriction of processing or object to processing of your personal data.'
      },
      {
        text: 'Request that a copy of your personal data be provided to yourself or a third party in digital format.'
      },
      { text: 'Withdraw consent for processing of your personal data.' }
    ],
    ADDITIONAL_TEXT:
      'We will respond to your request in writing—or orally if requested—as soon as possible and within one month at most after receiving it. In exceptional cases, we may extend this period by two months where necessary, taking into account the complexity and number of the requests.'
  },
  QUESTIONS: {
    TITLE: 'Questions, requests or concerns',
    TEXT: 'All questions, requests or concerns regarding this notice or related processing of your personal data—including all requests detailed in the "Your Rights" section above—should be directed to our data privacy team by email:'
  },
  FOOTER_NOTE:
    'This policy is provided for informational purposes and does not create any rights or obligations beyond those provided by applicable laws.'
};

export const JOB_DETAILS = {
  CONFIDENTIALITY: {
    TITLE: 'Confidentiality and Data Retention',
    TEXT: company => [
      `${company} will take reasonable steps to maintain the confidentiality of personal information (including appropriate technical measures against unauthorised or unlawful processing and against accidental loss or damage). All candidates are encouraged to read the company's relevant personal data privacy notice. If you believe that any of your personal information in our possession may be inaccurate or obsolete, please email ${company === 'MarQ Capital' ? 'recruiting@mqcapital.co.uk' : 'recruiting@spinnakeralgo.com'}. Candidates who have submitted a CV to ${company} and do not wish to proceed with their application should send an e-mail to ${company === 'MarQ Capital' ? 'recruiting@mqcapital.co.uk' : 'recruiting@spinnakeralgo.com'}. The personal information of candidates who have been the subject of background checks may have been transferred in accordance with applicable regulations and any concerns about this process should be addressed to ${company === 'MarQ Capital' ? 'recruiting@mqcapital.co.uk' : 'recruiting@spinnakeralgo.com'}.`,
      `${company} may retain candidate information for prospective recruitment activities, including sourcing candidates for job opportunities and organising events with potential candidates, in accordance with applicable privacy laws. The retention period will depend on requirements and legitimate interests which will be carefully considered. Candidates can find out more about our retention policies by emailing ${company === 'MarQ Capital' ? 'recruiting@mqcapital.co.uk' : 'recruiting@spinnakeralgo.com'}.`
    ]
  }
};
